// import uuid_1 from 'uuid';
// import sha1 from 'js-sha1';

import Cookies from 'js-cookie';

export interface User {
  id: string;
  name: string;
}
export enum MessageType {
  Text = 0,
  Card = 1,
  Other = 2
}
export enum ActionType {
  click = 'click',
  reply = 'reply',
  feedBack = 'feedback'
}
export interface Action {
  text: string;
  actionType: ActionType;
  url?: string;
  actionParams?: {
    [key: string]: string;
  };
}
export interface DUHints {
  kind: 'session' | 'turn';
  entities: {
    [entityType: string]: {
      [entityLabel: string]: string[];
    };
  };
}
export interface Message {
  id?: string;
  messageType: MessageType;
  title?: string;
  text: string;
  richMedia?: {
    fileURL: string;
    altText: string;
  };
  user: User;
  timestamp?: string;
  state?: string;
  actionList?: Action[];
  err?: string;
  notFirst?: boolean;
}

export interface FrameEvent {
  source?: string;
  type: string;
  packageName: string;
  slots?: { attribute: string; value: any; }[];
  jsonValue?: any;
}

export interface ExternalTryItNowConfig {
  url: string;
  user?: User;
  defaultParams?: {
    [key: string]: any;
  };
}
export type ContextGetter = () => {
  [key: string]: any;
};
export type ActionHandler = (action: Action) => void;


export class ExternalTryItNowClient {

  private url;
  private user?;
  private params;
  private history: any;
  private contextGetter?: any;
  private actionHandler?: any;

  constructor(config: ExternalTryItNowConfig) {
    this.history = [];
    this.url = config.url;
    this.user = config.user;
    this.params = config.defaultParams || {};
    const urlCache = window.localStorage.getItem('try-it-now-url');
    if (urlCache && urlCache !== 'undefined') {
      this.url = urlCache;
    }
    const userCache = window.localStorage.getItem('try-it-now-user');
    if (userCache && userCache !== 'undefined') {
      this.user = JSON.parse(userCache);
    }
  }
  static create(config: ExternalTryItNowConfig): ExternalTryItNowClient {
    return new ExternalTryItNowClient(config);
  }
  registerContextGetter(contextGetter: ContextGetter) {
    this.contextGetter = contextGetter;
  }
  registerActionHandler(handler: ActionHandler) {
    this.actionHandler = handler;
  }
  executeAction(action: Action) {
    if (!this.actionHandler) {
      throw new Error('no action handler registered');
    }
    this.actionHandler(action);
  }
  setUrl(url: string) {
    this.url = url;
    window.localStorage.setItem('try-it-now-url', url);
  }
  setUser(user: User, initialParams?: {
    [key: string]: any;
  }) {
    this.user = user;
    this.params = initialParams || new Map();
  }
  setDefaultParam(key: string, value: any) {
    this.params.set(key, value);
    // window.localStorage.setItem('copilot-initial-params', JSON.stringify(this.params));
  }
  getDefaultParam(key: string): any | undefined {
    return this.params.get(key);
  }
  setDefaultParams(params: {
    [key: string]: any;
  }): void {
    this.params = params;
    // window.localStorage.setItem('copilot-initial-params', JSON.stringify(params));
  }
  getDefaultParams(): {
    [key: string]: any;
  } {
    return this.params;
  }
  rresetDefaultParams(initialParams: {
    [key: string]: any;
  }): void {
    this.params = initialParams;
  }
  // send an empty message to connect with copilot bot
  async connect(params?: {
    [key: string]: any;
  }) {
    return this.sendMessage({
      text: '',
      messageType: MessageType.Text,
      user: this.user || { id: 'unknown', name: 'unknown' },
    }, true, params);
  }
  async sendMessage(
    message: Message,
    initial: boolean | undefined = false,
    extra?: {
      [key: string]: any;
    },
    events?: FrameEvent[]) {
    // const initial = this.history.length === 0;
    if (message.text && message.text.length) {
      this.history.push(message);
    }

    // 发送之前events做一次转换，slots里面的每个value需要判断是否为string type， 如果不是，则转换为string type
    events?.forEach((event) => {
      if (event.slots) {
        event.slots.forEach((slot) => {
          if (typeof slot.value !== 'string') {
            slot.value = JSON.stringify(slot.value);
          }
        });
      }
    });

    const response = await fetch(this.url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        events,
        extra,
        restart: initial,
        text: message.text,
        user: this.user && this.user.id || '',
        token: Cookies.get('token'),
        sessionId: getSessionId(this.user && this.user.id || ''),
        messageId: getMessageId(this.user && this.user.id || '')
      }),
    }).catch((err: any) => {
      throw err;
    });
    let res;
    if (!response.ok) {
      throw new Error(`Failed to send message. Status code ${response.status}`);
    } else {
      res = await response.json().catch((err: any) => {
        throw err;
      });
    }
    let receivedMessages = (res.response || []).map((item: any, index: number) => {
      return this.parseResponse(item, index);
    }).filter(Boolean);

    // 合并文本消息逻辑
    const textMessages = receivedMessages.filter((msg: Message) => msg.messageType === MessageType.Text);
    const cardMessages = receivedMessages.filter((msg: Message) => msg.messageType === MessageType.Card);
    const otherMessages = receivedMessages.filter((msg: Message) => msg.messageType === MessageType.Other);
    const mergedText = textMessages.map((msg: Message) => msg.text).join('\n\n');

    if (cardMessages.length > 0) {
      const firstCard = cardMessages[0];
      firstCard.text = `${mergedText}\n\n${firstCard.text}`;
      receivedMessages = [
        ...cardMessages.map((card: Message, index: number) => index === 0 ? firstCard : card),
        ...otherMessages
      ];
    } else if (textMessages.length > 0) {
      const newTextMessage: Message = {
        messageType: MessageType.Text,
        text: mergedText,
        user: { id: this.url, name: 'Copilot' },
        notFirst: false
      };
      receivedMessages = [newTextMessage, ...otherMessages];
    } else {
      receivedMessages = [...otherMessages];
    }

    this.history.push(...receivedMessages);
    if (this.history.length > 50) {
      this.history = this.history.slice(this.history.length - 50);
    }
    window.localStorage.setItem(this.getHistoryKey(), JSON.stringify(this.history));
    return receivedMessages;
  }
  getHistory() {
    if (!this.history.length) {
      const cache = window.localStorage.getItem(this.getHistoryKey());
      const history = cache && JSON.parse(cache);
      if (history && history.length) {
        this.history = history;
      }
    }
    return this.history;
  }
  clearHistory() {
    window.localStorage.removeItem(this.getHistoryKey());
    this.history = [];
  }
  getUser() {
    return this.user;
  }
  getHistoryKey() {
    return `copilot-history-${this.url}-${this.user && this.user.id}`;
  }
  parseResponse(res: any, index: number) {
    if (typeof res !== 'string') {
      return undefined;
    }
    let str = res;
    if (str.trim().startsWith('[')) {
      const end = res.indexOf(']');
      if (end !== -1) {
        str = str.substring(end + 1);
      }
    }
    if (!str || !str.length) {
      return undefined;
    }
    if (str.startsWith('{') && str.endsWith('}')) {
      try {
        const json = JSON.parse(str);
        return {
          messageType: MessageType.Card,
          title: json.title,
          text: json.description,
          user: {
            id: this.url,
            name: 'Copilot'
          },
          richMedia: json.richMedia && {
            fileURL: json.richMedia.fileUrl,
            altText: json.richMedia.altText
          },
          actionList: (json.insideAction || []).map((item: any) => ({
            actionType: item.type,
            url: item.url && item.url !== 'null' && item.url,
            text: item.display,
            actionParams: item.payload
          })),
          notFirst: index > 0
        };
      } catch (err: any) {
        return {
          messageType: MessageType.Text,
          text: str,
          user: {
            id: this.url,
            name: 'Copilot'
          },
          notFirst: index > 0
        };
      }
    }
    return {
      messageType: MessageType.Text,
      text: str,
      user: {
        id: this.url,
        name: 'Copilot'
      },
      notFirst: index > 0
    };
  }
}

function getSessionKey(userId: string): string {
  return `try-it-now-session-${userId}`;
}
function getSessionId(userId: string): string {
  let sessionId = window.sessionStorage.getItem(getSessionKey(userId));
  if (!sessionId) {
    sessionId = userId;
    window.sessionStorage.setItem(getSessionKey(userId), sessionId || '');
  }
  return sessionId || '';
}
function getMessageId(userId: string) {
  const timestamp = (new Date()).valueOf();
  return timestamp + userId;
}
